import { AsyncTask } from "../task/index.js";
import { InjectionToken } from "./token.js";
/**
 * Simple and slim DI container.
 */
export class PulseInjector {
    parent;
    providers = new Map();
    store = new Map();
    tasks = new Map();
    constructor(parent) {
        this.parent = parent;
    }
    register(...providers) {
        for (const provider of providers) {
            this.providers.set(provider.provide, provider);
        }
        return this;
    }
    /**
     * Register a factory function for a token.
     */
    factory(provider) {
        this.providers.set(provider.provide, provider);
        return this;
    }
    /**
     * Get a resolved value for provider.
     */
    get(token) {
        const value = this.getOrCreate(token);
        return this.isPromise(value) ? value : Promise.resolve(value);
    }
    /**
     * Get a resolved value for provider synchronously.
     *
     * *** Throws if one of the providers in the dependency graph is asynchronous.
     */
    getSync(token) {
        const value = this.getOrCreate(token);
        if (this.isPromise(value)) {
            const name = this.typeName(token);
            throw new Error([
                `Cannot instantiate dependency synchronously!`,
                `One of the providers of the dependencies of '${name}' is asynchronous!`,
            ].join('\n'));
        }
        return value;
    }
    getOrCreate(token) {
        if (token === PulseInjector) {
            return this;
        }
        const storeValue = this.store.get(token);
        if (storeValue) {
            return storeValue;
        }
        let task = this.tasks.get(token);
        if (task) {
            return task.waitUntilResolved();
        }
        task = new AsyncTask();
        const provider = this.providers.get(token);
        if (!provider) {
            if (this.parent) {
                return this.parent.getOrCreate(token);
            }
            throw new Error(`No provider found for type '${this.typeName(token)}'!`);
        }
        const deps = this.getDeps(provider);
        if (this.isPromise(deps)) {
            this.tasks.set(token, task);
            deps.then((params) => this.createValue(provider, params))
                .then((value) => {
                this.store.set(token, value);
                task?.resolve(value);
                this.tasks.delete(token);
            })
                .catch((ex) => {
                this.tasks.delete(token);
                task?.reject(ex);
            });
            return task.waitUntilResolved();
        }
        else {
            const value = this.createValue(provider, deps);
            this.store.set(token, value);
            return value;
        }
    }
    createValue(provider, params) {
        if (typeof provider.useValue !== 'undefined') {
            return provider.useValue;
        }
        else if (provider.useClass) {
            return new provider.useClass(...params);
        }
        else if (provider.useFactory) {
            return provider.useFactory(...params);
        }
        else if (this.isClass(provider.provide)) {
            return new provider.provide(...params);
        }
        else {
            const name = this.typeName(provider.provide);
            throw new Error(`No useClass or useFactory is provided for type '${name}'`);
        }
    }
    getDeps(provider) {
        this.checkDeps(provider);
        const deps = Array.isArray(provider.deps) ? provider.deps : [];
        const values = deps.map((d) => this.getOrCreate(d));
        const async = values.some((v) => this.isPromise(v));
        if (async) {
            return Promise.all(values);
        }
        return values;
    }
    checkDeps(provider) {
        const deps = Array.isArray(provider.deps) ? provider.deps : [];
        const providers = deps.map((d) => this.findProvider(d));
        const missing = deps.filter((_, i) => providers[i] === undefined);
        if (!missing.length) {
            return;
        }
        const typeName = this.typeName(provider.provide);
        const params = providers.map((p) => (p ? this.typeName(p.provide) : '?')).join(', ');
        const missingMsg = `No provider found for the following tokens: ${missing
            .map((m) => this.typeName(m))
            .join(', ')}`;
        const message = `Cannot instantiate dependency '${typeName}(${params})'!\n\n ${missingMsg}`;
        throw new Error(message);
    }
    findProvider(token) {
        return this.providers.get(token) || this.parent?.findProvider(token);
    }
    typeName(t) {
        if (t instanceof InjectionToken) {
            return `Token(${t.key})`;
        }
        if (this.isClass(t)) {
            return t.name;
        }
        else {
            return String(t);
        }
    }
    isClass(v) {
        return (v &&
            typeof v === 'function' &&
            typeof v.name === 'string' &&
            typeof v.constructor === 'function' &&
            v.name);
    }
    isPromise(v) {
        return typeof v === 'object' && (v instanceof Promise || (v.then && v.catch));
    }
}
//# sourceMappingURL=injector.js.map